import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Send, GraduationCap, User } from "lucide-react";

const roleStyle = {
  "élève": "bg-cyan-500/10 text-cyan-300 border-cyan-500/30",
  instructeur: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  invité: "bg-slate-700/40 text-slate-300 border-slate-600",
};

// Presentational comments thread. onPost(text, name?) handled by parent.
export default function CommentsPanel({ comments = [], onPost, guest = false }) {
  const [text, setText] = useState("");
  const [name, setName] = useState(localStorage.getItem("vfr_guest_name") || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      if (guest && name.trim()) localStorage.setItem("vfr_guest_name", name.trim());
      await onPost(text.trim(), name.trim());
      setText("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass rounded-2xl p-6" data-testid="comments-panel">
      <h3 className="mb-4 flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-cyan-400">
        <MessageSquare className="h-4 w-4" /> Échanges élève / instructeur
      </h3>

      {comments.length === 0 ? (
        <p className="mb-4 text-sm text-slate-500">Aucun commentaire pour l'instant.</p>
      ) : (
        <ul className="mb-5 space-y-3">
          {comments.map((c) => (
            <li key={c.id} className="rounded-xl border border-slate-800 bg-slate-900/40 p-3" data-testid={`comment-${c.id}`}>
              <div className="mb-1 flex items-center gap-2">
                <span className="flex items-center gap-1 text-sm font-medium text-slate-200">
                  {c.role === "instructeur" ? <GraduationCap className="h-3.5 w-3.5 text-amber-400" /> : <User className="h-3.5 w-3.5 text-cyan-400" />}
                  {c.author_name}
                </span>
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${roleStyle[c.role] || roleStyle["invité"]}`}>
                  {c.role}
                </span>
                <span className="ml-auto font-mono-data text-[10px] text-slate-500">
                  {(c.created_at || "").slice(0, 16).replace("T", " ")}
                </span>
              </div>
              <p className="whitespace-pre-wrap text-sm text-slate-300">{c.text}</p>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-2">
        {guest && (
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Votre nom (ex. FI Martin)"
            data-testid="comment-name-input" className="border-slate-700 bg-slate-900/60 text-slate-100" />
        )}
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Écrire un commentaire…"
          data-testid="comment-text-input" className="border-slate-700 bg-slate-900/60 text-slate-100" />
        <div className="flex justify-end">
          <Button onClick={submit} disabled={busy || !text.trim()} data-testid="comment-submit-button"
            className="bg-cyan-500 font-semibold text-[#070C18] hover:bg-cyan-400">
            <Send className="mr-1.5 h-4 w-4" /> Envoyer
          </Button>
        </div>
      </div>
    </div>
  );
}
